import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { NavigationElement, PublishedNavigationElement } from "@/lib/api";
import { dashboardNavigationElementsQuery } from "@/features/admin/hooks/queries";
import { dashboardQuery, useDashboardData } from "@/features/shared/hooks";

import { getNavigationElementBySlug, getSlugPathForPageId } from "./navigation.utils";

/**
 * Pure: compute the dashboard (public) path from CMS context.
 * Used when linking from admin to the same resource in the dashboard.
 */
export function getDashboardPath({
  dashboard,
  navigationElements,
  pageId,
}: {
  dashboard?: { slug: string };
  navigationElements: NavigationElement[];
  pageId?: string;
}): string {
  if (!dashboard?.slug) return "/";
  const slugPath = pageId ? getSlugPathForPageId(navigationElements, pageId) : undefined;
  return slugPath?.length
    ? `/${dashboard.slug}/${slugPath.join("/")}`
    : `/${dashboard.slug}`;
}

/**
 * Pure: compute the admin/CMS path from dashboard context.
 * Used when linking from dashboard to the same resource in the CMS.
 */
export function getAdminPath({
  dashboardId,
  navigationElements,
  pageSlug,
  subPageSlug,
}: {
  dashboardId?: string;
  navigationElements: PublishedNavigationElement[];
  pageSlug?: string;
  subPageSlug?: string;
}): string {
  const currentPage = getNavigationElementBySlug(navigationElements, pageSlug, subPageSlug);
  const pageId = currentPage?.page_id;
  if (dashboardId && pageId) {
    return `/admin/dashboard/${dashboardId}/page/${pageId}`;
  }
  if (dashboardId) {
    return `/admin/dashboard/${dashboardId}`;
  }
  return "/admin";
}

/**
 * Hook: dashboard path for the current CMS resource.
 * Use in admin/header when rendering the "go to dashboard" link.
 */
export function useDashboardPath(): string {
  const params = useParams<{ dashboard?: string; page?: string }>();
  const dashboardId = params.dashboard; // Guid
  const dashboard = useQuery({
    ...dashboardQuery(dashboardId ?? ""),
    enabled: Boolean(dashboardId),
  });
  const navigationElementsQuery = useQuery({
    ...dashboardNavigationElementsQuery(dashboardId ?? ""),
    enabled: Boolean(dashboardId),
  });

  return getDashboardPath({
    dashboard: dashboard.data ?? undefined,
    navigationElements: navigationElementsQuery.data ?? [],
    pageId: params.page,
  });
}

/**
 * Hook: admin path for the current dashboard resource.
 * Use in dashboard nav when rendering the "Admin" link.
 */
export function useAdminPath(): string {
  const params = useParams<{
    dashboard?: string;
    page?: string;
    subPage?: string;
  }>();
  const { selectedDashboard, navigationElements } = useDashboardData();
  return getAdminPath({
    dashboardId: selectedDashboard?.id,
    navigationElements: navigationElements ?? [],
    pageSlug: params.page,
    subPageSlug: params.subPage,
  });
}
